import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Loader2, PackagePlus, X } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", category: "Vêtements", sku: "", barcode: "", purchase_price: 0, price: 0, sale_price: "", images: [""], variants: [], stock_min: 5, supplier: "", online_visible: true, featured: false };

export default function StoreStock() {
  const { slug } = useParams();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/store/${slug}/products`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const openCreate = () => { setEditing(null); setF({ ...empty, variants: [{ color: "", size: "", quantity: 0 }] }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setF({ ...empty, ...p, sale_price: p.sale_price || "", images: p.images?.length ? p.images : [""] }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f, purchase_price: Number(f.purchase_price) || 0, price: Number(f.price) || 0,
        sale_price: f.sale_price === "" ? null : Number(f.sale_price), stock_min: Number(f.stock_min) || 0,
        images: f.images.filter(Boolean),
        variants: f.variants.map((v) => ({ ...v, quantity: Number(v.quantity) || 0 })),
      };
      if (editing) await api.put(`/store/${slug}/products/${editing.id}`, payload);
      else await api.post(`/store/${slug}/products`, payload);
      toast.success("Produit enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const del = async (p) => { if (!window.confirm("Supprimer ce produit ?")) return; await api.delete(`/store/${slug}/products/${p.id}`); load(); toast.success("Supprimé"); };

  const addVariant = () => setF({ ...f, variants: [...f.variants, { color: "", size: "", quantity: 0 }] });
  const setVariant = (i, k, v) => setF({ ...f, variants: f.variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) });
  const rmVariant = (i) => setF({ ...f, variants: f.variants.filter((_, idx) => idx !== i) });

  return (
    <div>
      <PageHeader title="Stock" subtitle="Produits, variantes et niveaux de stock">
        <Button onClick={openCreate} className="vp-accent-bg text-black font-medium" data-testid="stock-add-btn"><Plus size={16} className="mr-1" /> Nouveau produit</Button>
      </PageHeader>

      {rows === null ? <div className="text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((p) => {
            const stock = (p.variants || []).reduce((s, v) => s + v.quantity, 0);
            const low = stock <= (p.stock_min || 5);
            return (
              <div key={p.id} className="vp-card rounded-xl overflow-hidden hover-lift" data-testid="stock-product-row">
                <div className="flex gap-3 p-3">
                  <div className="h-20 w-20 rounded-lg bg-black/30 overflow-hidden shrink-0">{p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="text-sm font-medium text-white line-clamp-1">{p.name}</div>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-white" data-testid="stock-edit-btn"><Pencil size={14} /></button>
                        <button onClick={() => del(p)} className="text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mono">{p.sku}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="mono text-sm vp-accent-text">{fmtMoney(p.sale_price || p.price)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full mono ${low ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>stk {stock}</span>
                    </div>
                  </div>
                </div>
                <div className="px-3 pb-3 flex flex-wrap gap-1">
                  {(p.variants || []).map((v, i) => <span key={i} className="text-xs bg-white/5 rounded px-1.5 py-0.5 text-slate-300 mono">{v.color}/{v.size}:{v.quantity}</span>)}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && <p className="text-slate-500 col-span-full text-center py-10">Aucun produit</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="vp-card border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Nom</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-black/30 border-white/10 text-white" data-testid="stock-field-name" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Catégorie</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">SKU</Label><Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Prix d'achat</Label><Input type="number" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Prix de vente</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="bg-black/30 border-white/10 text-white" data-testid="stock-field-price" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Prix promo (optionnel)</Label><Input type="number" value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: e.target.value })} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Stock minimum</Label><Input type="number" value={f.stock_min} onChange={(e) => setF({ ...f, stock_min: e.target.value })} className="bg-black/30 border-white/10 text-white" /></div>
              <div className="col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Image (URL)</Label><Input value={f.images[0]} onChange={(e) => setF({ ...f, images: [e.target.value] })} className="bg-black/30 border-white/10 text-white" /></div>
              <div className="col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Description</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-black/30 border-white/10 text-white" /></div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={f.online_visible} onCheckedChange={(v) => setF({ ...f, online_visible: v })} /> Visible en ligne</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} /> Produit vedette</label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-slate-300 text-sm">Variantes (couleur / taille / quantité)</Label>
                <Button size="sm" variant="ghost" onClick={addVariant} className="text-slate-300 hover:text-white h-7"><PackagePlus size={14} className="mr-1" /> Ajouter</Button></div>
              <div className="space-y-2">
                {f.variants.map((v, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                    <Input placeholder="Couleur" value={v.color} onChange={(e) => setVariant(i, "color", e.target.value)} className="bg-black/30 border-white/10 text-white h-9" />
                    <Input placeholder="Taille" value={v.size} onChange={(e) => setVariant(i, "size", e.target.value)} className="bg-black/30 border-white/10 text-white h-9" />
                    <Input type="number" placeholder="Qté" value={v.quantity} onChange={(e) => setVariant(i, "quantity", e.target.value)} className="bg-black/30 border-white/10 text-white h-9 mono" />
                    <button onClick={() => rmVariant(i)} className="text-slate-500 hover:text-red-400"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={save} disabled={saving} className="vp-accent-bg text-black font-medium" data-testid="stock-save-btn">{saving && <Loader2 className="animate-spin mr-2" size={15} />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
